/**
 * /publisher/releases/new — the release workflow.
 *
 * In order: release type, the music, release information, cover art, lyrics, outlets,
 * and review. Publisher access covers submission, so Review releases directly. The retained
 * distributor-validation step is feature-disabled until that integration is ready.
 *
 * A FULL PAGE, NOT A DRAWER AND NOT A STUDIO TOOL. §4 and §7 both rule that out, and the
 * route lives at a top-level path under the Publisher's own entitlement gate — see the
 * Publisher block in src/router.tsx.
 *
 * ============================================================================
 * WHAT THIS SCREEN CAN AND CANNOT DO, STATED ONCE.
 *
 * Everything up to submission is real and persists: the draft is a record in
 * publisherStore (localStorage-backed, §28), the songs come from the actual Music
 * Library, the artwork upload really reads the user's file, the completeness checks
 * really run, and every field maps to a field the committed LabelGrid spec names.
 *
 * The prototype outlet catalogue follows LabelGrid's public supported-store directory.
 * The retained validation step cannot obtain LabelGrid's verdict and is currently hidden.
 * Submission records a local queue receipt until provider submission exists; it never
 * invents a provider id or outlet delivery state.
 * ============================================================================
 *
 * STEPS ARE NAVIGABLE, NOT LOCKED IN SEQUENCE. §15 requires the review step to send the
 * user back to whatever needs fixing, and a strictly forward wizard makes that five
 * presses of Back. The rail marks what is outstanding; submission is what is gated.
 *
 * A draft is created as soon as a new wizard opens. Every field and step update is then
 * persisted against that draft, so leaving or refreshing the wizard never loses progress.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck } from 'lucide-react';
import {
  BackLink,
  Btn,
  LoadError,
  LoadSkeleton,
  PrototypeNotice,
  SectionHeading,
} from '../components/ui';
import { RadioCard, RadioCardGroup, Select, TextField } from '../components/forms';
import {
  ArtworkStep,
  IssueList,
  OutletPicker,
  ReleaseSummary,
  SongPickRow,
  WizardStepRail,
} from '../components/publisher';
import type { ArtworkSource, WizardStep } from '../components/publisher';
import { publisherApi } from '../api/publisherApi';
import type { LabelGridOutlet } from '../api/labelgrid';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { USE_MOCK_API } from '../config';
import { GENRES } from '../data/music';
import { isPublisherOutletAvailable } from '../data/publisherOutlets';
import { usePublisher, useSongs } from '../hooks';
import { readImageAsDataUrl, validateImageFile } from '../lib/imageUpload';
import { isUploadedSong } from '../lib/license';
import {
  normalizePublisherTrackLyrics,
  publisherLyricsForTrack,
  publisherLyricsPatch,
} from '../lib/publisherLyrics';
import {
  formatReleaseDate,
  languageLabel,
  outletCountLabel,
  PUBLISHER_LANGUAGE_OPTIONS,
  trackCountLabel,
} from '../lib/publisherFormat';
import { selectProfileName, useAuthStore } from '../stores/authStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { isFirstLoad } from '../stores/loadState';
import { publisherCreditsLeft, usePublisherCreditsStore } from '../stores/publisherCreditsStore';
import { useSongsStore } from '../stores/songsStore';
import { c } from '../styles/theme';
import type {
  PublisherRelease,
  PublisherReleaseType,
  PublisherSettingsPayload,
  Song,
} from '../types/domain';
import { paths } from './paths';

/**
 * The eight steps, and their order.
 *
 * Labels are short because the rail is one scrolling line on a phone — the panel's own
 * heading carries the full sentence.
 */
const VALIDATION_STEP_ENABLED = false;
const REVIEW_STEP = VALIDATION_STEP_ENABLED ? 8 : 7;

const STEPS: readonly WizardStep[] = [
  { number: 1, label: 'Type' },
  { number: 2, label: 'Music' },
  { number: 3, label: 'Information' },
  { number: 4, label: 'Cover art' },
  { number: 5, label: 'Lyrics' },
  { number: 6, label: 'Outlets' },
  ...(VALIDATION_STEP_ENABLED ? [{ number: 7, label: 'Validation' }] : []),
  { number: REVIEW_STEP, label: 'Review' },
];

/**
 * The release types offered, with the reason each exists.
 *
 * THREE OF LABELGRID'S SIX DOCUMENTED `content_type` VALUES — see
 * LABELGRID_CONTENT_TYPES, which is read from the committed spec. §8 says to implement
 * only types that are actually supported; all three of these are, and Compilation, Mix
 * and Podcast are omitted as a product decision rather than because they are unavailable.
 * The descriptions are Lyrixa's own guidance, not a claim about LabelGrid's rules — the
 * spec sets no track-count constraint per content type.
 */
const RELEASE_TYPES: readonly { value: PublisherReleaseType; hint: string }[] = [
  { value: 'Single', hint: 'One track, sometimes with a second version.' },
  { value: 'EP', hint: 'A short set — typically three to six tracks.' },
  { value: 'Album', hint: 'A full-length record.' },
];

function explicitDefaultFromSettings(settings: PublisherSettingsPayload | null): boolean | null {
  const value = settings?.distributionPreferences.explicitContent.value;
  if (value === 'explicit') return true;
  if (value === 'clean') return false;
  return null;
}

type InformationField =
  'title' | 'artistName' | 'writerContributor' | 'genre' | 'language' | 'explicit';

function commonSongValue(songs: readonly Song[], read: (song: Song) => string | undefined): string {
  const values = songs.map((song) => read(song)?.trim() ?? '').filter(Boolean);
  if (values.length !== songs.length || values.length === 0) return '';
  const first = values[0];
  return values.every((value) => value.toLowerCase() === first.toLowerCase()) ? first : '';
}

function publisherLanguageCode(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';
  return (
    PUBLISHER_LANGUAGE_OPTIONS.find(
      (option) =>
        option.value.toLowerCase() === normalized || option.label.toLowerCase() === normalized
    )?.value ?? ''
  );
}

function publisherGenreOptions(value: string): readonly string[] {
  const genre = value.trim();
  if (!genre || GENRES.some((option) => option.toLowerCase() === genre.toLowerCase())) {
    return GENRES;
  }
  return [genre, ...GENRES];
}

function informationPrefillFor(input: {
  release: PublisherRelease;
  songs: readonly Song[];
  settings: PublisherSettingsPayload | null;
  profileName: string;
  allowExplicitPrefill: boolean;
  touchedFields: ReadonlySet<InformationField>;
}): Partial<PublisherRelease> {
  const patch: Partial<PublisherRelease> = {};
  const singleSong = input.songs.length === 1 ? input.songs[0] : null;
  const title = singleSong?.title.trim() ?? '';
  const songArtist = commonSongValue(input.songs, (song) => song.artistName);
  const artistName = songArtist || input.profileName.trim();
  const songGenre = commonSongValue(input.songs, (song) => song.genre);
  const genre =
    songGenre || input.settings?.distributionPreferences.defaultGenre.value.trim() || '';
  const songLanguage = commonSongValue(input.songs, (song) => song.language);
  const language = publisherLanguageCode(songLanguage);
  const explicit = explicitDefaultFromSettings(input.settings);

  if (!input.touchedFields.has('title') && !input.release.title.trim() && title) {
    patch.title = title;
  }
  if (!input.touchedFields.has('artistName') && !input.release.artistName.trim() && artistName) {
    patch.artistName = artistName;
  }
  if (
    !input.touchedFields.has('writerContributor') &&
    !input.touchedFields.has('artistName') &&
    !(input.release.writerContributor ?? '').trim() &&
    artistName
  ) {
    patch.writerContributor = artistName;
  }
  if (!input.touchedFields.has('genre') && !input.release.genre.trim() && genre)
    patch.genre = genre;
  if (!input.touchedFields.has('language') && !input.release.language.trim() && language) {
    patch.language = language;
  }
  if (
    input.allowExplicitPrefill &&
    !input.touchedFields.has('explicit') &&
    explicit === true &&
    !input.release.explicit
  ) {
    patch.explicit = true;
  }

  return patch;
}

function artworkSourceFor(release: PublisherRelease): ArtworkSource {
  if (release.artworkOrigin === 'generate' || (release.artworkPrompt ?? '').trim())
    return 'generate';
  if (release.artworkUrl) return 'upload';
  return 'song';
}

type PublisherSettingsLoad =
  | { status: 'idle'; data: null }
  | { status: 'loading'; data: PublisherSettingsPayload | null }
  | { status: 'ready'; data: PublisherSettingsPayload | null }
  | { status: 'error'; data: null };

export default function PublisherReleaseWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { songs } = useSongs();
  const profileName = useAuthStore(selectProfileName);
  const hasCommercialLicense = useEntitlementsStore((state) => state.commercial);
  const hasUnlimitedPlan = useEntitlementsStore((state) => state.unlimited);
  const publisherCreditsAvailable = usePublisherCreditsStore(publisherCreditsLeft);
  const publisherCreditsUnlimited = usePublisherCreditsStore((state) => state.unlimited);
  const {
    releases,
    createRelease,
    updateRelease,
    addEvent,
    load: publisherLoad,
    submit,
    submitting,
    issuesFor,
    setWizardProgress,
    wizardProgressFor,
    setLastVisibleSurface,
  } = usePublisher();

  /*
    A draft can be resumed. `?release=<id>` is how a release's detail page sends an
    unsubmitted draft back here to be edited (§15's Edit action), so the wizard is one
    screen rather than a near-copy of itself for editing.
  */
  const resumeId = searchParams.get('release');
  const [releaseId, setReleaseId] = useState<string | null>(resumeId);
  const release = releases.find((r) => r.id === releaseId) ?? null;
  const savedProgress = releaseId ? wizardProgressFor(releaseId) : null;
  const lastHydratedProgress = useRef<string | null>(null);

  const [step, setStep] = useState(savedProgress?.step ?? 1);
  const [visited, setVisited] = useState<number[]>(savedProgress?.visited ?? [1]);
  // Used for the initial render while the new draft is being created.
  const [draftType, setDraftType] = useState<PublisherReleaseType>('Single');
  const draftCreationStarted = useRef(false);

  // ---- step 4 state. The file is read here and stored on the release.
  const [artworkSource, setArtworkSource] = useState<ArtworkSource>('song');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const [settingsLoad, setSettingsLoad] = useState<PublisherSettingsLoad>({
    status: 'idle',
    data: null,
  });
  const createdReleaseIds = useRef<Set<string>>(new Set());
  const touchedInformationFields = useRef<Map<string, Set<InformationField>>>(new Map());

  // ---- step 6 state. The catalogue is fetched, not written.
  const [outlets, setOutlets] = useState<LabelGridOutlet[]>([]);
  const [loadingOutlets, setLoadingOutlets] = useState(true);
  const [showQueueReceipt, setShowQueueReceipt] = useState(false);

  /*
    THE LIBRARY'S READ STATE, for step 2 — which is the only step on this screen that
    renders a collection it did not create. Read from the store rather than widened into
    `useSongs`, the way Library.tsx and Playlist.tsx read it: one field per selector, and
    `load` is one store's own fact.

    Step 2 branched on `songs.length === 0` alone and told the user "Your library is empty,
    so there is nothing to release yet" with a button to Create — during the fetch, and
    permanently after a failed one. That is the same collapse of three states into one that
    Library.tsx's comment describes, and here it is worse in one respect: the user is midway
    through assembling a release, so being told their library is empty suggests the tracks
    they already selected are gone too.
  */
  const songsLoad = useSongsStore((s) => s.load);
  const loadSongs = useSongsStore((s) => s.loadSongs);

  // ---- step 7 state. The distributor's verdict, or why it could not be obtained.
  const [validating, setValidating] = useState(false);
  const [providerRefusal, setProviderRefusal] = useState<string | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);

  /*
    Publisher settings, fetched once on mount.

    In backend mode this is the user's saved Publisher profile; in mock mode the boundary
    asserts instead, so the wizard skips that read and falls back to whatever is already in
    the local draft plus the signed-in profile name.
  */
  useEffect(() => {
    let live = true;
    if (USE_MOCK_API) {
      setSettingsLoad({ status: 'ready', data: null });
      return () => {
        live = false;
      };
    }

    setSettingsLoad({ status: 'loading', data: null });
    void publisherApi
      .getPublisherSettings()
      .then((data) => {
        if (live) setSettingsLoad({ status: 'ready', data });
      })
      .catch(() => {
        if (live) setSettingsLoad({ status: 'error', data: null });
      });

    return () => {
      live = false;
    };
  }, []);

  /*
    The outlet catalogue, fetched once on mount.

    Through the API boundary like every other read — the empty result is the API's
    answer, not a decision this component made. See publisherApi.getOutlets, whose own
    note is §13.
  */
  useEffect(() => {
    let live = true;
    void publisherApi
      .getOutlets()
      .then((list) => {
        if (live) setOutlets(list);
      })
      .catch(() => {
        // An empty catalogue and a failed read look the same to the user, and both are
        // honest: OutletPicker explains that no list can be shown either way.
        if (live) setOutlets([]);
      })
      .finally(() => {
        if (live) setLoadingOutlets(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const issues = useMemo(() => (release ? issuesFor(release) : []), [release, issuesFor]);
  const blockedSteps = useMemo(
    () => [...new Set(issues.filter((i) => i.blocking).map((i) => i.step))],
    [issues]
  );
  /** Songs on this release, in track order, skipping any deleted from the library. */
  const releaseSongs = useMemo<Song[]>(
    () =>
      release
        ? release.songIds
            .map((id) => songs.find((s) => s.id === id))
            .filter((s): s is Song => Boolean(s))
        : [],
    [release, songs]
  );

  const unavailableSongReasons = useMemo(() => {
    const reasons = new Map<string, string>();
    const statusPriority: Record<PublisherRelease['status'], number> = {
      live: 3,
      in_review: 2,
      processing: 2,
      draft: 1,
      failed: 0,
      takedown: 0,
    };
    const blockingReleases = releases
      .filter(
        (candidate) =>
          candidate.id !== releaseId &&
          ['draft', 'in_review', 'processing', 'live'].includes(candidate.status)
      )
      .sort((left, right) => statusPriority[right.status] - statusPriority[left.status]);

    for (const candidate of blockingReleases) {
      const reason =
        candidate.status === 'live'
          ? 'This track is already live.'
          : candidate.status === 'draft'
            ? 'This track is already included in another draft release.'
            : 'This track has been submitted and is awaiting release.';
      for (const songId of candidate.songIds) {
        if (!reasons.has(songId)) reasons.set(songId, reason);
      }
    }
    return reasons;
  }, [releaseId, releases]);

  useEffect(() => {
    if (resumeId || releaseId || draftCreationStarted.current) return;
    if (!USE_MOCK_API && (publisherLoad.status === 'idle' || publisherLoad.status === 'loading')) {
      return;
    }
    draftCreationStarted.current = true;
    const created = createRelease(draftType);
    createdReleaseIds.current.add(created.id);
    setReleaseId(created.id);
    setWizardProgress(created.id, { step: 1, visited: [1], phase: 'wizard' });
    setSearchParams({ release: created.id }, { replace: true });
  }, [
    createRelease,
    draftType,
    publisherLoad.status,
    releaseId,
    resumeId,
    setSearchParams,
    setWizardProgress,
  ]);

  useEffect(() => {
    if (!savedProgress || !releaseId) return;
    const key = JSON.stringify(savedProgress);
    if (lastHydratedProgress.current === key) return;
    lastHydratedProgress.current = key;
    setStep(
      !VALIDATION_STEP_ENABLED && savedProgress.step === 8 ? REVIEW_STEP : savedProgress.step
    );
    setVisited(savedProgress.visited);
  }, [releaseId, savedProgress]);

  useEffect(() => {
    if (releaseId != null && !release) return;
    setLastVisibleSurface(
      releaseId == null ? { kind: 'wizard', releaseId: null } : { kind: 'wizard', releaseId }
    );
  }, [release, releaseId, setLastVisibleSurface]);

  useEffect(() => {
    if (!releaseId) return;
    if (
      savedProgress?.step === step &&
      savedProgress.phase === 'wizard' &&
      savedProgress.visited.length === visited.length &&
      savedProgress.visited.every((value, index) => value === visited[index])
    ) {
      return;
    }
    setWizardProgress(releaseId, { step, visited, phase: 'wizard' });
  }, [releaseId, savedProgress, setWizardProgress, step, visited]);

  useEffect(() => {
    if (!release) return;
    setArtworkSource(artworkSourceFor(release));
  }, [release]);

  useEffect(() => {
    if (!release || releaseSongs.length === 0) return;
    const defaults = Object.fromEntries(releaseSongs.map((song) => [song.id, song.lyrics]));
    const normalized = normalizePublisherTrackLyrics(release, defaults);
    const current = release.trackLyrics ?? {};
    const currentKeys = Object.keys(current).sort();
    const normalizedKeys = Object.keys(normalized).sort();
    const changed =
      currentKeys.length !== normalizedKeys.length ||
      normalizedKeys.some(
        (key, index) => key !== currentKeys[index] || current[key] !== normalized[key]
      );
    const patch = publisherLyricsPatch(release.songIds, normalized);
    if (changed || release.lyrics !== patch.lyrics) updateRelease(release.id, patch);
  }, [release, releaseSongs, updateRelease]);

  useEffect(() => {
    if (!release) return;
    if (step < 3) return;

    const patch = informationPrefillFor({
      release,
      songs: releaseSongs,
      settings: settingsLoad.data,
      profileName,
      allowExplicitPrefill: createdReleaseIds.current.has(release.id),
      touchedFields:
        touchedInformationFields.current.get(release.id) ?? new Set<InformationField>(),
    });

    if (Object.keys(patch).length > 0) {
      updateRelease(release.id, patch);
    }
    if (settingsLoad.status === 'ready' || settingsLoad.status === 'error') {
      createdReleaseIds.current.delete(release.id);
    }
  }, [
    profileName,
    release,
    releaseSongs,
    settingsLoad.data,
    settingsLoad.status,
    step,
    updateRelease,
  ]);

  function goTo(next: number) {
    if (next === REVIEW_STEP && showQueueReceipt) setShowQueueReceipt(false);
    setStep(next);
    setVisited((seen) => (seen.includes(next) ? seen : [...seen, next]));
  }

  function startRelease() {
    if (release) {
      goTo(2);
      return;
    }
  }

  function toggleSong(songId: string) {
    if (!release) return;
    const song = songs.find((candidate) => candidate.id === songId);
    const alreadySelected = release.songIds.includes(songId);
    if (!alreadySelected && song && isUploadedSong(song)) return;
    if (!alreadySelected && unavailableSongReasons.has(songId)) return;
    if (
      !alreadySelected &&
      !publisherCreditsUnlimited &&
      release.songIds.length >= publisherCreditsAvailable
    ) {
      return;
    }
    const next = alreadySelected
      ? release.songIds.filter((id) => id !== songId)
      : [...release.songIds, songId];
    const defaults = Object.fromEntries(songs.map((candidate) => [candidate.id, candidate.lyrics]));
    const trackLyrics = normalizePublisherTrackLyrics(release, defaults);
    if (alreadySelected) delete trackLyrics[songId];
    else trackLyrics[songId] = song?.lyrics ?? '';
    updateRelease(release.id, {
      songIds: next,
      rightsAttested: false,
      ...publisherLyricsPatch(next, trackLyrics),
    });
  }

  function updateTrackLyrics(songId: string, value: string) {
    if (!release) return;
    const trackLyrics = normalizePublisherTrackLyrics(release);
    trackLyrics[songId] = value;
    updateRelease(release.id, publisherLyricsPatch(release.songIds, trackLyrics));
  }

  function updateArtistName(value: string) {
    if (!release) return;
    const writerWasDefault =
      !touchedInformationFields.current.get(release.id)?.has('writerContributor') &&
      (!(release.writerContributor ?? '').trim() ||
        (release.writerContributor ?? '').trim() === release.artistName.trim());
    markInformationTouched(release.id, 'artistName');
    updateRelease(release.id, {
      artistName: value,
      ...(writerWasDefault ? { writerContributor: value } : {}),
    });
  }

  function toggleOutlet(outletKey: string) {
    if (!release) return;
    const outlet = outlets.find((candidate) => candidate.key === outletKey);
    if (outlet && !isPublisherOutletAvailable(outlet, hasUnlimitedPlan)) return;
    const next = release.outletKeys.includes(outletKey)
      ? release.outletKeys.filter((k) => k !== outletKey)
      : [...release.outletKeys, outletKey];
    updateRelease(release.id, { outletKeys: next });
  }

  useEffect(() => {
    if (!release || loadingOutlets || hasUnlimitedPlan) return;
    const availableKeys = new Set(
      outlets
        .filter((outlet) => isPublisherOutletAvailable(outlet, false))
        .map((outlet) => outlet.key)
    );
    const permitted = release.outletKeys.filter((key) => availableKeys.has(key));
    if (permitted.length !== release.outletKeys.length) {
      updateRelease(release.id, { outletKeys: permitted });
    }
  }, [hasUnlimitedPlan, loadingOutlets, outlets, release, updateRelease]);

  function markInformationTouched(releaseId: string, field: InformationField) {
    const touched = touchedInformationFields.current.get(releaseId) ?? new Set<InformationField>();
    touched.add(field);
    touchedInformationFields.current.set(releaseId, touched);
  }

  function updateInformationField<K extends InformationField>(
    releaseId: string,
    field: K,
    value: PublisherRelease[K]
  ) {
    markInformationTouched(releaseId, field);
    updateRelease(releaseId, { [field]: value } as Partial<PublisherRelease>);
  }

  /**
   * Reads a picked cover image onto the release.
   *
   * Validated with the app's existing rules (format and size, lib/imageUpload) and NOT
   * with invented ones — §11. Writes `artworkUrl` on the RELEASE; `Song.coverUrl` is
   * never touched from this screen.
   */
  async function handleUpload(file: File) {
    if (!release) return;
    setUploadError(null);
    setGenerateError(null);
    const rejection = validateImageFile(file);
    if (rejection) {
      setUploadError(rejection.message);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readImageAsDataUrl(file);
      // Clearing artworkFromSongId as well: the release has one cover, and leaving the
      // song reference behind would make "where did this image come from" ambiguous.
      updateRelease(release.id, {
        artworkUrl: dataUrl,
        artworkFromSongId: null,
        artworkOrigin: 'upload',
      });
      setUploadedName(`${file.name} will be uploaded as the release cover.`);
      addEvent(release.id, 'Cover art uploaded.', {
        type: 'milestone',
        detail: `${file.name} is stored on the release and will be used as the submitted cover image.`,
        actionLabel: 'Resume release',
      });
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : 'That image could not be read. Try another file.'
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerateArtwork() {
    if (!release) return;
    const artworkPrompt = release.artworkPrompt?.trim() ?? '';
    if (!artworkPrompt) {
      setGenerateError('Enter a prompt before asking Lyrixsa to generate cover art.');
      return;
    }
    if (USE_MOCK_API) {
      setGenerateError(
        'Generated cover art needs the backend configured for Publisher artwork generation.'
      );
      return;
    }

    setUploadError(null);
    setGenerateError(null);
    setGenerating(true);
    try {
      const updated = await publisherApi.generateReleaseArtwork({
        release,
        artworkPrompt,
      });
      updateRelease(release.id, updated);
      setUploadedName(null);
      addEvent(release.id, 'Cover art generated.', {
        type: 'milestone',
        detail:
          'A generated image was saved on this release draft and will be used as the submitted cover image.',
        actionLabel: 'Resume release',
      });
    } catch (error) {
      setGenerateError(
        error instanceof Error
          ? error.message
          : 'Cover art could not be generated right now. Try again.'
      );
    } finally {
      setGenerating(false);
    }
  }

  /**
   * Asks LabelGrid to validate the release.
   *
   * §14 says to use `POST /releases/{release}/validate` rather than building a fake
   * validation system, so this really calls the boundary — and the boundary refuses,
   * because there is no backend and no release exists at LabelGrid to validate. The
   * refusal is shown as a refusal. A green "Validation passed" here would be the fake
   * system the brief forbids.
   */
  async function runProviderValidation() {
    if (!release) return;
    setValidating(true);
    setProviderRefusal(null);
    try {
      const result = await publisherApi.validateRelease(release.id, {
        release,
        songs: releaseSongs,
      });
      // Unreachable in this build: validateRelease throws. Written so the success path
      // exists and is typed the day a backend answers.
      setProviderRefusal(
        result.result === 'OK'
          ? null
          : result.errors.join(' ') || 'The distributor reported validation errors.'
      );
    } catch (error) {
      setProviderRefusal(
        error instanceof Error ? error.message : 'The distributor could not be reached.'
      );
    } finally {
      setValidating(false);
    }
  }

  async function handleSubmit() {
    if (!release) return;
    setSubmitError(null);
    const result = await submit(release.id);
    if (result.ok) setShowQueueReceipt(true);
    else setSubmitError(result.message);
  }

  /*
    A resumed draft that no longer exists.

    Reachable by pasting a stale URL or by opening a link after clearing storage. Says
    what happened rather than rendering an empty wizard that would create a second draft
    on the first edit.
  */
  if (resumeId && !release) {
    return (
      <div>
        <BackLink fallbackTo={paths.publisherDashboard} fallbackLabel="Publisher" mode="parent" />
        <div className="lyx-wizard-panel">
          <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: c.text1 }}>
            That release could not be found
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: c.text2, lineHeight: 1.55 }}>
            It may have been deleted, or the link may be from a different browser — releases are
            stored locally in this build.
          </p>
          <Btn onClick={() => navigate(paths.publisherDashboard)}>Back to the Publisher</Btn>
        </div>
      </div>
    );
  }

  const hasBlockingIssues = issues.some((i) => i.blocking);
  const submissionReceived =
    release?.status === 'in_review' ||
    release?.status === 'processing' ||
    release?.status === 'live';

  return (
    <div>
      <BackLink fallbackTo={paths.publisherDashboard} fallbackLabel="Publisher" mode="parent" />

      <div style={{ marginBottom: 18 }}>
        <SectionHeading
          as="h1"
          title="NEW RELEASE"
          subtitle={
            release?.title.trim()
              ? `${release.title.trim()} · ${release.releaseType}`
              : 'Assemble a release from your library, then send it to the stores you choose.'
          }
          marginBottom={0}
        />
      </div>

      <WizardStepRail
        steps={STEPS}
        current={step}
        onSelect={goTo}
        blocked={blockedSteps}
        visited={visited}
      />

      <div className="lyx-wizard-panel">
        {/* ---- STEP 1 — release type (§8) ---- */}
        {step === 1 && (
          <div>
            <StepHeading
              title="What are you releasing?"
              description="This sets the release’s content type at the distributor."
            />
            <RadioCardGroup label="Release type">
              {RELEASE_TYPES.map((type) => (
                <RadioCard
                  key={type.value}
                  name="publisher-release-type"
                  value={type.value}
                  checked={(release?.releaseType ?? draftType) === type.value}
                  onSelect={() => {
                    if (release) updateRelease(release.id, { releaseType: type.value });
                    else setDraftType(type.value);
                  }}
                  ariaLabel={`${type.value} — ${type.hint}`}
                >
                  <span>
                    <span style={{ display: 'block', fontSize: 13, color: c.text1 }}>
                      {type.value}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: c.text2 }}>
                      {type.hint}
                    </span>
                  </span>
                </RadioCard>
              ))}
            </RadioCardGroup>

            <div className="lyx-wizard-actions">
              <Btn variant="secondary" onClick={() => navigate(paths.publisherDashboard)}>
                Cancel
              </Btn>
              <Btn onClick={startRelease} disabled={!release}>
                Continue
                <ArrowRight size={13} />
              </Btn>
            </div>
          </div>
        )}

        {/* ---- STEP 2 — the music (§9) ---- */}
        {step === 2 && release && (
          <div>
            <StepHeading
              title="Choose the music"
              description="Your Lyrixsa library, in the order you select. Nothing here is created for the Publisher — these are your real songs."
            />
            {/* The library's read states ahead of the empty one, for the reason
                  `songsLoad` is declared for. The order is Library.tsx's: skeleton only
                  while a FIRST read is in flight, failure only when there is nothing
                  behind it, and the designed empty state last — where it now means what
                  it says. */}
            {isFirstLoad(songsLoad, songs.length > 0) ? (
              <LoadSkeleton rows={4} label="Loading your songs…" />
            ) : songsLoad.status === 'error' && songs.length === 0 ? (
              <LoadError
                message={songsLoad.error?.message ?? UNKNOWN_API_ERROR_MESSAGE}
                onRetry={loadSongs}
              >
                <p style={{ fontSize: 12, color: c.text2, margin: '6px 0 0', lineHeight: 1.55 }}>
                  Your library could not be loaded, so there is nothing to choose from. This is not
                  a statement that you have no songs, and any tracks already on this release are
                  still on it.
                </p>
              </LoadError>
            ) : songs.length === 0 ? (
              <div>
                <p style={{ margin: '0 0 14px', fontSize: 12, color: c.text2, lineHeight: 1.55 }}>
                  Your library is empty, so there is nothing to release yet. Generate a song first
                  and it will appear here.
                </p>
                <Btn variant="secondary" onClick={() => navigate(paths.create)}>
                  Go to Create
                </Btn>
              </div>
            ) : (
              <>
                <div className="lyx-pick-list" role="group" aria-label="Your songs">
                  {songs.map((song) => {
                    const index = release.songIds.indexOf(song.id);
                    const selectionLimitReached =
                      index === -1 &&
                      !publisherCreditsUnlimited &&
                      release.songIds.length >= publisherCreditsAvailable;
                    const unavailableReason =
                      index === -1 ? unavailableSongReasons.get(song.id) : undefined;
                    return (
                      <SongPickRow
                        key={song.id}
                        song={song}
                        checked={index !== -1}
                        onToggle={toggleSong}
                        selectionBlocker={
                          unavailableReason ??
                          (selectionLimitReached
                            ? `You have ${publisherCreditsAvailable} Publisher Credit${publisherCreditsAvailable === 1 ? '' : 's'} available.`
                            : undefined)
                        }
                        position={index === -1 ? undefined : index + 1}
                      />
                    );
                  })}
                </div>
                <p style={{ margin: '10px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
                  {trackCountLabel(release.songIds.length)} selected.{' '}
                  {publisherCreditsUnlimited
                    ? 'Your plan includes unlimited publishing.'
                    : `${publisherCreditsAvailable} Publisher Credit${publisherCreditsAvailable === 1 ? '' : 's'} available.`}{' '}
                  Uploaded songs remain visible in your library, but are disabled here because
                  Lyrixsa cannot validate their commercial licensing for delivery.
                </p>
              </>
            )}
            <StepNav onBack={() => goTo(1)} onNext={() => goTo(3)} />
          </div>
        )}

        {/* ---- STEP 3 — release information (§10) ---- */}
        {step === 3 && release && (
          <div>
            <StepHeading
              title="Release information"
              description="Every field here backs a field the distributor’s API requires. Nothing is collected that a release does not need."
            />
            <TextField
              label="Release title"
              value={release.title}
              onChange={(value) => updateInformationField(release.id, 'title', value)}
              placeholder="e.g. Midnight Signals"
              marginBottom={12}
            />
            <TextField
              label="Artist name"
              value={release.artistName}
              onChange={updateArtistName}
              placeholder="The name this is credited to"
              marginBottom={12}
            />
            <TextField
              label="Writer / contributor"
              value={release.writerContributor ?? ''}
              onChange={(value) => updateInformationField(release.id, 'writerContributor', value)}
              placeholder="Name of the writer or contributor"
              marginBottom={12}
            />
            <Select
              label="Primary genre"
              value={release.genre}
              onChange={(value) => updateInformationField(release.id, 'genre', value)}
              options={publisherGenreOptions(release.genre)}
              placeholder="Choose a genre"
              marginBottom={12}
            />
            <Select
              label="Language of the recording"
              value={release.language}
              onChange={(value) => updateInformationField(release.id, 'language', value)}
              options={PUBLISHER_LANGUAGE_OPTIONS}
              placeholder="Choose a language"
              marginBottom={12}
            />
            {/* A real <input type="date">, so the stored value is already the ISO form
                  the API wants and the user gets their platform's own picker. */}
            <TextField
              label="Release date (optional)"
              type="date"
              value={release.releaseDate}
              onChange={(value) => updateRelease(release.id, { releaseDate: value })}
              disabled
              marginBottom={6}
            />
            <p style={{ margin: '0 0 14px', fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
              {/* Stated because a required-looking optional field is its own kind of
                    dishonesty: the spec does not list release_date as required. */}
              Optional at the distributor — leave it blank and the release is scheduled when it is
              approved.
            </p>

            <RadioCardGroup label="Explicit content">
              <RadioCard
                name="publisher-explicit"
                value="off"
                checked={!release.explicit}
                onSelect={() => updateInformationField(release.id, 'explicit', false)}
                size="compact"
                ariaLabel="Not explicit"
              >
                <span style={{ fontSize: 12.5, color: c.text1 }}>Not explicit</span>
              </RadioCard>
              <RadioCard
                name="publisher-explicit"
                value="on"
                checked={release.explicit}
                onSelect={() => updateInformationField(release.id, 'explicit', true)}
                size="compact"
                ariaLabel="Explicit lyrics"
              >
                <span style={{ fontSize: 12.5, color: c.text1 }}>Explicit lyrics</span>
              </RadioCard>
            </RadioCardGroup>

            <StepNav onBack={() => goTo(2)} onNext={() => goTo(4)} />
          </div>
        )}

        {/* ---- STEP 4 — cover art (§11) ---- */}
        {step === 4 && release && (
          <div>
            <StepHeading
              title="Cover art"
              description="The release’s own cover. Choosing one here never changes a song’s artwork in your library."
            />
            <ArtworkStep
              release={release}
              songs={releaseSongs}
              source={artworkSource}
              onSourceChange={(source) => {
                setArtworkSource(source);
                setUploadError(null);
                setGenerateError(null);
              }}
              onReuseSong={(songId) =>
                // The song's id, not its image: a reference, so the song's own artwork
                // is read rather than copied and never written. §11.
                updateRelease(release.id, {
                  artworkFromSongId: songId,
                  artworkUrl: null,
                  artworkOrigin: null,
                })
              }
              onUpload={(file) => void handleUpload(file)}
              onArtworkPromptChange={(prompt) =>
                updateRelease(release.id, { artworkPrompt: prompt })
              }
              onGenerate={() => void handleGenerateArtwork()}
              onClear={() => {
                updateRelease(release.id, {
                  artworkUrl: null,
                  artworkFromSongId: null,
                  artworkOrigin: null,
                });
                setUploadedName(null);
                setUploadError(null);
                setGenerateError(null);
              }}
              uploading={uploading}
              uploadError={uploadError}
              uploadedName={uploadedName}
              generating={generating}
              generateError={generateError}
            />
            <StepNav onBack={() => goTo(3)} onNext={() => goTo(5)} />
          </div>
        )}

        {/* ---- STEP 5 — lyrics (§12) ---- */}
        {step === 5 && release && (
          <div>
            <StepHeading
              title="Lyrics"
              description="Review or edit the lyrics for each track. Lyrics are optional."
            />
            <div style={{ display: 'grid', gap: 10 }}>
              {releaseSongs.map((song, index) => {
                const value = publisherLyricsForTrack(release, song.id);
                const editor = (
                  <div style={{ padding: release.releaseType === 'Single' ? 0 : '12px 12px 4px' }}>
                    <TextField
                      label={
                        release.releaseType === 'Single' ? 'Lyrics' : `Lyrics for ${song.title}`
                      }
                      value={value}
                      onChange={(next) => updateTrackLyrics(song.id, next)}
                      multiline
                      rows={12}
                      placeholder="Paste or write the lyrics for this track."
                      marginBottom={6}
                    />
                    {song.lyrics.trim() && value !== song.lyrics && (
                      <Btn
                        variant="secondary"
                        onClick={() => updateTrackLyrics(song.id, song.lyrics)}
                        aria-label={`Restore the saved lyrics for ${song.title}`}
                      >
                        Restore saved lyrics
                      </Btn>
                    )}
                  </div>
                );
                if (release.releaseType === 'Single') return <div key={song.id}>{editor}</div>;
                return (
                  <details
                    key={song.id}
                    style={{
                      border: `1px solid ${c.border}`,
                      borderRadius: 10,
                      background: c.surface1,
                      padding: 12,
                    }}
                  >
                    <summary
                      aria-label={`Track ${index + 1}: ${song.title}, ${value.trim() ? 'lyrics added' : 'no lyrics'}`}
                      style={{ cursor: 'pointer', fontSize: 12 }}
                    >
                      <strong>
                        {index + 1}. {song.title}
                      </strong>
                      <span style={{ marginLeft: 8, color: c.text2 }}>
                        {value.trim() ? 'Lyrics added' : 'No lyrics'}
                      </span>
                    </summary>
                    {editor}
                  </details>
                );
              })}
            </div>
            <StepNav onBack={() => goTo(4)} onNext={() => goTo(6)} />
          </div>
        )}

        {/* ---- STEP 6 — outlets (§13) ---- */}
        {step === 6 && release && (
          <div>
            <StepHeading
              title="Where should it go?"
              description="The stores and streaming services come from the distributor’s own outlet catalogue."
            />
            <OutletPicker
              outlets={outlets}
              selected={release.outletKeys}
              onToggle={toggleOutlet}
              hasUnlimitedPlan={hasUnlimitedPlan}
              loading={loadingOutlets}
              unavailableReason="The outlet list is a LabelGrid read this build cannot make."
            />
            {release.outletKeys.length > 0 && (
              <p style={{ margin: '10px 0 0', fontSize: 11, color: c.text2 }}>
                {outletCountLabel(release.outletKeys.length)} selected.
              </p>
            )}
            <StepNav
              onBack={() => goTo(5)}
              onNext={() => goTo(VALIDATION_STEP_ENABLED ? 7 : REVIEW_STEP)}
              nextLabel={VALIDATION_STEP_ENABLED ? undefined : 'Continue to review'}
            />
          </div>
        )}

        {/* ---- STEP 7 — validation (§14) ---- */}
        {VALIDATION_STEP_ENABLED && step === 7 && release && (
          <div>
            <StepHeading
              title="Action Required"
              description="Two sets of checks, kept separate: Lyrixsa’s own, which have run, and the distributor’s, which are the ones that decide."
            />

            <p className="lyx-ws-eyebrow" style={{ margin: '0 0 8px' }}>
              Lyrixsa checks
            </p>
            <IssueList
              issues={issues}
              onGoToStep={goTo}
              emptyMessage="Every field Lyrixsa checks is filled in."
            />

            <p className="lyx-ws-eyebrow" style={{ margin: '18px 0 8px' }}>
              Distributor checks
            </p>
            {/*
                The real endpoint, called for real, refusing for real. §14 forbids a fake
                validation system, and the honest rendering of "we could not ask" is not a
                tick — it is this.
              */}
            <div
              style={{
                border: `1px solid ${c.border}`,
                background: c.surface2,
                borderRadius: 9,
                padding: '12px 13px',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 12,
                  color: c.text2,
                  lineHeight: 1.55,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 7,
                }}
              >
                <ShieldCheck size={13} color={c.text2} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Not run. The distributor’s verdict comes from{' '}
                  <span style={{ fontFamily: 'IBM Plex Mono' }}>
                    POST /releases/{'{release}'}/validate
                  </span>
                  , which needs the release to exist at LabelGrid and a server-side token to call
                  it.
                </span>
              </p>
              <Btn
                variant="secondary"
                onClick={() => void runProviderValidation()}
                disabled={validating}
              >
                {validating ? (
                  <Loader2 size={13} className="lyx-spin" />
                ) : (
                  <ShieldCheck size={13} />
                )}
                {validating ? 'Asking the distributor…' : 'Run distributor validation'}
              </Btn>
              {providerRefusal && (
                <p
                  style={{
                    margin: '10px 0 0',
                    fontSize: 11.5,
                    color: c.danger,
                    lineHeight: 1.55,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 6,
                  }}
                >
                  <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                  {providerRefusal}
                </p>
              )}
            </div>

            <StepNav onBack={() => goTo(6)} onNext={() => goTo(8)} nextLabel="Continue to review" />
          </div>
        )}

        {/* ---- STEP 8 — review (§15) ---- */}
        {step === REVIEW_STEP && release && showQueueReceipt ? (
          <PrototypeNotice
            style={{
              margin: 0,
              borderColor: c.accentBorder,
              background: c.accentSoft,
              color: c.text1,
            }}
          >
            Your release has been received and is currently in the distribution queue. We’ll notify
            you when it moves to the next stage.
          </PrototypeNotice>
        ) : step === REVIEW_STEP && release ? (
          <div>
            <StepHeading
              title="Review"
              description="Everything as it will be sent. Edit any step from here."
            />

            <ReleaseSummary
              rows={[
                { label: 'Type', value: release.releaseType },
                { label: 'Title', value: release.title.trim() },
                { label: 'Artist', value: release.artistName.trim() },
                { label: 'Writer / contributor', value: release.writerContributor?.trim() },
                {
                  label: 'Tracks',
                  value:
                    releaseSongs.length > 0
                      ? releaseSongs.map((s) => s.title).join(', ')
                      : trackCountLabel(0),
                },
                { label: 'Genre', value: release.genre },
                { label: 'Language', value: languageLabel(release.language) },
                { label: 'Release date', value: formatReleaseDate(release.releaseDate) },
                { label: 'Explicit', value: release.explicit ? 'Yes' : 'No' },
                {
                  label: 'Cover art',
                  value: release.artworkUrl
                    ? release.artworkOrigin === 'generate'
                      ? 'Generated image'
                      : 'Uploaded image'
                    : release.artworkFromSongId
                      ? 'Reusing a song’s artwork'
                      : 'Placeholder gradient — not an image file',
                },
                {
                  label: 'Lyrics',
                  value:
                    releaseSongs.length > 0
                      ? releaseSongs
                          .map(
                            (song) =>
                              `${song.title}: ${publisherLyricsForTrack(release, song.id).trim() ? 'Included' : 'None'}`
                          )
                          .join(' · ')
                      : 'None',
                },
                {
                  label: 'Outlets',
                  value: `${outletCountLabel(release.outletKeys.length)} selected`,
                },
                {
                  /* §24/§25 — the provider-assigned identifiers, shown as unassigned
                       rather than filled with a plausible number. Both are assigned by
                       LabelGrid (barcode_number auto-generates, isrc is issued), so a
                       value here before submission would be fabricated. */
                  label: 'UPC / ISRC',
                  value: 'Assigned by the distributor after submission',
                },
              ]}
            />

            <div style={{ marginTop: 18 }}>
              <IssueList
                issues={issues}
                onGoToStep={goTo}
                emptyMessage="Every field Lyrixsa checks is filled in."
              />
            </div>

            {!submissionReceived && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 9,
                  marginTop: 16,
                  padding: '12px 14px',
                  border: `1px solid ${c.border}`,
                  borderRadius: 8,
                  background: c.surface2,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={release.rightsAttested === true}
                  onChange={(event) =>
                    updateRelease(release.id, { rightsAttested: event.target.checked })
                  }
                  style={{ margin: '2px 0 0', flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: c.text1, lineHeight: 1.55 }}>
                  I attest that I have all rights and permissions required to release and distribute
                  every track in this release.
                </span>
              </label>
            )}

            {submissionReceived ? (
              <div
                role="status"
                style={{
                  marginTop: 18,
                  padding: '14px 16px',
                  border: `1px solid ${c.success}`,
                  background: c.successSoft,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <ShieldCheck size={17} color={c.success} style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ margin: 0, color: c.text1, fontSize: 12.5, lineHeight: 1.6 }}>
                  Your release has been received and is currently in the distribution queue. We’ll
                  notify you when it moves to the next stage.
                </p>
              </div>
            ) : (
              <div className="lyx-wizard-actions">
                <Btn variant="secondary" onClick={() => goTo(VALIDATION_STEP_ENABLED ? 7 : 6)}>
                  <ArrowLeft size={13} />
                  Edit
                </Btn>
                <Btn
                  onClick={() => void handleSubmit()}
                  disabled={
                    !hasCommercialLicense || hasBlockingIssues || submitting.includes(release.id)
                  }
                  aria-describedby={
                    !hasCommercialLicense
                      ? 'publisher-commercial-license-required'
                      : hasBlockingIssues
                        ? 'publisher-review-blocked'
                        : undefined
                  }
                >
                  {submitting.includes(release.id) ? (
                    <Loader2 size={13} className="lyx-spin" />
                  ) : (
                    <Check size={13} />
                  )}
                  Release
                </Btn>
              </div>
            )}
            {!hasCommercialLicense && !submissionReceived && (
              <PrototypeNotice
                style={{ marginTop: 12, borderColor: c.accentBorder, background: c.accentSoft }}
              >
                <span id="publisher-commercial-license-required">
                  Purchase the Commercial License before releasing music.
                </span>
              </PrototypeNotice>
            )}
            {hasBlockingIssues && !submissionReceived && (
              <p
                id="publisher-review-blocked"
                style={{ margin: '10px 0 0', fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}
              >
                Release is unavailable while a required item is outstanding.
              </p>
            )}
            {submitError && !submissionReceived && (
              <p style={{ margin: '10px 0 0', fontSize: 11.5, color: c.danger, lineHeight: 1.55 }}>
                {submitError}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A step's heading and its one-line explanation. */
function StepHeading({ title, description }: { title: string; description: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2
        style={{
          fontFamily: 'Space Grotesk',
          fontWeight: 600,
          fontSize: 14,
          margin: '0 0 4px',
          color: c.text1,
        }}
      >
        {title}
      </h2>
      <p style={{ margin: 0, fontSize: 12, color: c.text2, lineHeight: 1.55 }}>{description}</p>
    </div>
  );
}

/**
 * Back and Continue.
 *
 * Continue is never disabled on the intermediate steps: the rail and the validation step
 * are where outstanding items are reported, and blocking forward movement per step would
 * make a release impossible to walk through while the audio blocker stands. The gate is
 * at submission, which is where §14 puts it.
 */
function StepNav({
  onBack,
  onNext,
  nextLabel = 'Continue',
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
}) {
  return (
    <div className="lyx-wizard-actions">
      <Btn variant="secondary" onClick={onBack}>
        <ArrowLeft size={13} />
        Back
      </Btn>
      <Btn onClick={onNext}>
        {nextLabel}
        <ArrowRight size={13} />
      </Btn>
    </div>
  );
}
